// ============================================================================
// Layer housing helpers — Phase 2.1 (extracted ahead of Round 1 modals)
// ============================================================================
// setHousingAnchorFromReport : after a layer daily with a hen count lands,
//                              update the housing anchor (current_count +
//                              current_count_date) that projected-count math
//                              is derived from. Used by WebformHub,
//                              AdminAddReportModal, LayerDailysView,
//                              LayerDailyPage. The housing is matched here;
//                              the write goes through the
//                              set_layer_housing_anchor RPC (migration 220).
// computeProjectedCount      : projected = anchor - mortalities since anchor.
//                              Used everywhere the layer dashboards render
//                              live hen counts.
// computeLayerFeedCost       : snapshot-based cost helper — takes the batch's
//                              frozen per-lb rates, returns total $.
//
// Verbatim extraction from main.jsx lines 592–675. Behavior unchanged; only
// the file they live in moves.
// ============================================================================

import type {SupabaseClient} from './supabaseTypes.js';

// Row shapes below name ONLY the columns these helpers read. Counts and ids are
// `number | string` because the same helpers run over PostgREST rows, jsonb
// blobs and form values, and every read already goes through parseInt/String.
// Everything else stays `unknown` behind an index signature: there are no
// generated database types to check a fuller shape against.
export type LayerHousingRow = {
  id?: string | number | null;
  housing_name?: string | null;
  batch_id?: string | number | null;
  status?: string | null;
  current_count?: number | string | null;
  current_count_date?: string | null;
  start_date?: string | null;
  deleted_at?: string | null;
  [column: string]: unknown;
};

export type LayerDailyRow = {
  date?: string | null;
  batch_label?: string | null;
  batch_id?: string | number | null;
  layer_count?: number | string | null;
  mortality_count?: number | string | null;
  [column: string]: unknown;
};

// layer_batches as the batch-name fallback selects it.
type LayerBatchNameRow = {id: string | number; name?: string | null};

// The batch's frozen per-lb rates, as computeLayerFeedCost reads them.
export type LayerFeedRateBatch = {
  per_lb_starter_cost?: number | string | null;
  per_lb_grower_cost?: number | string | null;
  per_lb_layer_cost?: number | string | null;
};

// Every outcome setHousingAnchorFromReport can report. Callers branch on `ok`
// and then on `reason`, so the two are modelled as one discriminated union
// rather than a bag of optional fields.
export type HousingAnchorResult =
  | {ok: false; reason: 'no-input' | 'empty-name' | 'db-error'}
  | {ok: false; reason: 'ambiguous-batch'; batchName: string | null | undefined; housingNames: (string | null)[]}
  | {ok: false; reason: 'no-match'; name: string | number | null | undefined}
  | {ok: false; reason: 'update-failed'; error: string}
  | {
      ok: true;
      id: string | number | null | undefined;
      housingName: string | null | undefined;
      newCount: number;
      newDate: string | null | undefined;
    };

// parseInt/parseFloat declare a `string` parameter, but these helpers are fed
// raw row values (numbers, nulls, undefined) and rely on the runtime coercion
// those functions already perform. The cast keeps each call exactly as it was
// rather than introducing String(), which would change behaviour for objects
// and arrays. Same convention as src/lib/cattleNutrition.ts.
// The browser-side housing MATCH, without any write. Shared by the anchor
// below and by the migration-234 create operation, which sends the matched
// housing id so the server can anchor inside the report's own transaction.
// The match rule is unchanged: a direct housing_name, then the batch-name
// fallback only when that batch has exactly one active housing.
export type HousingResolveResult =
  | {ok: false; reason: 'empty-name' | 'db-error'}
  | {ok: false; reason: 'ambiguous-batch'; batchName: string | null | undefined; housingNames: (string | null)[]}
  | {ok: false; reason: 'no-match'; name: string | number | null | undefined}
  | {ok: true; target: LayerHousingRow};

export async function resolveHousingForReport(
  sb: SupabaseClient,
  housingOrBatchName: string | number | null | undefined,
): Promise<HousingResolveResult> {
  const trimmed = String(housingOrBatchName ?? '')
    .toLowerCase()
    .trim();
  if (!trimmed) return {ok: false, reason: 'empty-name'};
  // Step 1 — try direct housing_name match
  const {data: housings, error: selErr} = await sb
    .from('layer_housings')
    .select('id,current_count,current_count_date,status,housing_name,batch_id')
    .eq('status', 'active');
  if (selErr || !housings) return {ok: false, reason: 'db-error'};
  let target: LayerHousingRow | undefined = housings.find(
    (h: LayerHousingRow) =>
      String(h.housing_name || '')
        .toLowerCase()
        .trim() === trimmed,
  );
  // Step 2 — fall back to batch-name match if no housing matched directly
  // (e.g. report's batch_label is "L-26-01" not "Eggmobile 2")
  if (!target) {
    const {data: batches} = await sb.from('layer_batches').select('id,name');
    const matchingBatch = (batches || []).find(
      (b: LayerBatchNameRow) =>
        String(b.name || '')
          .toLowerCase()
          .trim() === trimmed,
    );
    if (matchingBatch) {
      const batchHousings = housings.filter((h: LayerHousingRow) => h.batch_id === matchingBatch.id);
      if (batchHousings.length === 1) {
        target = batchHousings[0];
      } else if (batchHousings.length > 1) {
        return {
          ok: false,
          reason: 'ambiguous-batch',
          batchName: matchingBatch.name,
          housingNames: batchHousings.map((h: LayerHousingRow) => h.housing_name ?? null),
        };
      }
    }
  }
  if (!target) return {ok: false, reason: 'no-match', name: housingOrBatchName};
  return {ok: true, target};
}

export async function setHousingAnchorFromReport(
  sb: SupabaseClient,
  housingOrBatchName: string | number | null | undefined,
  newCount: number | string | null | undefined,
  reportDate: string | null | undefined,
): Promise<HousingAnchorResult> {
  if (!housingOrBatchName || newCount == null || isNaN(parseInt(newCount as string)))
    return {ok: false, reason: 'no-input'};
  const newC = Math.max(0, parseInt(newCount as string));
  const resolved = await resolveHousingForReport(sb, housingOrBatchName);
  if (!resolved.ok) return resolved;
  const target = resolved.target;
  // The WRITE is a SECURITY DEFINER RPC (migration 220, S2): the S2 table
  // takes direct layer_housings UPDATE away from farm_team and light, and a
  // PostgREST UPDATE on a row a policy hides affects 0 rows WITHOUT an error,
  // so a direct update would report success and write nothing. The match above
  // stays here, unchanged; the server re-checks that the id is still an ACTIVE
  // housing and refuses otherwise, which surfaces as 'update-failed'.
  const {error: updErr} = await sb.rpc('set_layer_housing_anchor', {
    p_housing_id: target.id,
    p_count: newC,
    p_date: reportDate || null,
  });
  if (updErr) return {ok: false, reason: 'update-failed', error: updErr.message};
  return {ok: true, id: target.id, housingName: target.housing_name, newCount: newC, newDate: reportDate};
}

// ── Canonical housing ↔ layer-daily matching ───────────────────────────────
// One shared rule for every surface that assigns a layer_dailys row to a
// housing (Animals on Farm history, Home snapshot, layer dashboards, feed
// planning):
//   1. An exact normalized batch_label ↔ housing_name match wins.
//   2. A label that names a DIFFERENT housing never matches this one, even
//      when both housings share a batch_id (Eggmobile 2 and Layer Schooner
//      both live under l-26-01; a Layer Schooner row is not Eggmobile 2's).
//   3. A row with no housing-specific label (blank, or a batch-level label
//      such as the batch name) may fall back to batch_id ONLY when the batch
//      has exactly one known housing. An ambiguous multi-housing batch fails
//      closed instead of guessing.
// Pass the full housing roster so sibling ambiguity is visible. Without a
// roster the housing itself is the only known housing, which preserves the
// legacy single-housing batch fallback.

function normHousingName(value: unknown): string {
  return String(value || '')
    .toLowerCase()
    .trim();
}

function sameHousing(a: LayerHousingRow | null | undefined, b: LayerHousingRow | null | undefined): boolean {
  if (!a || !b) return false;
  if (a === b) return true;
  return a.id != null && b.id != null && String(a.id) === String(b.id);
}

export type LayerDailyHousingMatcher = (
  daily: LayerDailyRow | null | undefined,
  housing: LayerHousingRow | null | undefined,
) => boolean;

// The parameter admits null/undefined ELEMENTS, not out of caution but because
// `.filter(Boolean)` below has always been the roster's only membership rule and
// callers pass `[housing]` straight through. The predicate form of that filter
// is what carries the narrowing into the loop.
//
// It is `readonly` because this function only READS the roster — the filter
// builds a fresh array and nothing writes back. Callers that hold a readonly
// roster (animalHistory's layer evidence walk) can therefore pass it directly
// instead of copying it or asserting the copy away.
export function createLayerDailyHousingMatcher(
  allHousings: readonly (LayerHousingRow | null | undefined)[] | null | undefined,
): LayerDailyHousingMatcher {
  const roster = (Array.isArray(allHousings) ? allHousings : []).filter((h): h is LayerHousingRow => Boolean(h));
  const nameOwners = new Map<string, LayerHousingRow[]>(); // normalized housing_name -> housings claiming it
  const batchHousings = new Map<string, LayerHousingRow[]>(); // String(batch_id) -> non-deleted housings
  for (const h of roster) {
    const name = normHousingName(h.housing_name);
    if (name) {
      if (!nameOwners.has(name)) nameOwners.set(name, []);
      // `?.` rather than an assertion: the set above guarantees the entry, and
      // optional chaining states that without claiming anything the checker
      // cannot see.
      nameOwners.get(name)?.push(h);
    }
    if (h.batch_id != null && !h.deleted_at) {
      const key = String(h.batch_id);
      if (!batchHousings.has(key)) batchHousings.set(key, []);
      batchHousings.get(key)?.push(h);
    }
  }
  return function matchesHousing(daily, housing) {
    if (!daily || !housing) return false;
    const label = normHousingName(daily.batch_label);
    const housingName = normHousingName(housing.housing_name);
    if (label && housingName && label === housingName) return true;
    // Housing-specific label owned by another housing: never reassign it.
    if (label && (nameOwners.get(label) || []).some((h) => !sameHousing(h, housing))) return false;
    // Batch-id fallback only when this housing is the batch's only housing.
    if (housing.batch_id == null || daily.batch_id == null) return false;
    if (String(daily.batch_id) !== String(housing.batch_id)) return false;
    const siblings = batchHousings.get(String(housing.batch_id)) || [];
    return !siblings.some((h) => !sameHousing(h, housing));
  };
}

export function layerDailyMatchesHousing(
  daily: LayerDailyRow | null | undefined,
  housing: LayerHousingRow | null | undefined,
  allHousings?: (LayerHousingRow | null | undefined)[] | null,
): boolean {
  const roster = Array.isArray(allHousings) && allHousings.length > 0 ? allHousings : [housing];
  return createLayerDailyHousingMatcher(roster)(daily, housing);
}

// Resolve the display hen count for a housing — positive current_count when
// present, otherwise the latest positive matching layer_dailys.layer_count.
// NO mortality subtraction. Use this for dashboard totals, chips, and any
// surface labeled "hens" or "current count". Pass allHousings so a sibling
// housing's daily rows cannot be claimed through the shared batch_id.
export function computeHousingDisplayCount(
  housing: LayerHousingRow | null | undefined,
  layerDailys: LayerDailyRow[] | null | undefined,
  allHousings?: (LayerHousingRow | null | undefined)[] | null,
): number {
  if (!housing) return 0;
  const parsed = housing.current_count != null ? parseInt(housing.current_count as string) : NaN;
  if (parsed > 0) return parsed;

  const matchesHousing = createLayerDailyHousingMatcher(
    Array.isArray(allHousings) && allHousings.length > 0 ? allHousings : [housing],
  );
  const matches = (layerDailys || [])
    .filter((d) => d && d.layer_count != null && parseInt(d.layer_count as string) > 0 && matchesHousing(d, housing))
    .sort((a, b) => (b.date || '').localeCompare(a.date || ''));
  if (matches.length === 0) return parsed >= 0 ? parsed : 0;
  const count = parseInt(matches[0].layer_count as string);
  return isNaN(count) ? 0 : count;
}

export type ProjectedCount = {anchor: number; anchorDate: string | null; projected: number; mortSince: number};

// Compute projected count for a housing.
// projected = anchor - sum(mortalities reported between anchorDate and today)
// Use this only where mortality-adjusted counts are explicitly intended.
// Pass allHousings so the daily-anchor fallback uses the shared matching rule.
// Returns {anchor, anchorDate, projected, mortSince} or null if no anchor.
// Both collection parameters are `readonly`: this helper only filters, sorts a
// filtered COPY, and reduces. Callers that already hold immutable rosters
// (broiler's layer-feed month walk, feedPlanner's poultry burn context) pass
// them straight in.
export function computeProjectedCount(
  housing: LayerHousingRow | null | undefined,
  layerDailys: readonly LayerDailyRow[] | null | undefined,
  allHousings?: readonly (LayerHousingRow | null | undefined)[] | null,
): ProjectedCount | null {
  if (!housing) return null;
  const parsed = housing.current_count != null ? parseInt(housing.current_count as string) : NaN;
  let anchor: number | null = isNaN(parsed) ? null : parsed;
  let anchorDate: string | null = housing.current_count_date || housing.start_date || null;

  const matchesHousing = createLayerDailyHousingMatcher(
    Array.isArray(allHousings) && allHousings.length > 0 ? allHousings : [housing],
  );

  function latestDailyAnchor(): {count: number; date: string | null} | null {
    const matches = (layerDailys || [])
      .filter((d) => d && d.layer_count != null && parseInt(d.layer_count as string) > 0 && matchesHousing(d, housing))
      .sort((a, b) => (b.date || '').localeCompare(a.date || ''));
    if (matches.length === 0) return null;
    const count = parseInt(matches[0].layer_count as string);
    if (isNaN(count)) return null;
    return {count, date: matches[0].date || null};
  }

  if (!(anchor != null && anchor > 0)) {
    const daily = latestDailyAnchor();
    if (daily) {
      if (
        anchor === 0 &&
        housing.status !== 'active' &&
        housing.current_count_date &&
        // `daily.date != null` is added because `>` needs two comparable
        // operands. It changes nothing: a null right-hand side made the
        // original comparison numeric (NaN > 0), which is already false, so
        // both spellings take the else branch.
        daily.date != null &&
        housing.current_count_date > daily.date
      ) {
        // Intentional zero on a retired/inactive housing with a date newer than the daily
      } else {
        anchor = daily.count;
        anchorDate = daily.date;
      }
    } else if (anchor == null) {
      return null;
    }
  }

  // Unreachable in practice, and stated as a guard rather than an assertion:
  // the ONLY path that leaves `anchor` null is the `daily === null && anchor ==
  // null` branch above, which has already returned. Nothing is claimed here
  // that the code does not itself enforce.
  if (anchor == null) return null;

  if (!anchorDate) {
    return {anchor, anchorDate: null, projected: anchor, mortSince: 0};
  }
  // Captured as a const so the narrowing above survives into the filter
  // closure: TypeScript resets a `let`'s narrowing inside a callback, because
  // it cannot prove the binding was not reassigned in between.
  const sinceDate = anchorDate;
  const todayStr = new Date().toISOString().split('T')[0];
  const mortSince = (layerDailys || [])
    .filter((d) => {
      if (!d || !d.date || !d.batch_label) return false;
      if (String(d.batch_label).toLowerCase().trim() !== String(housing.housing_name).toLowerCase().trim())
        return false;
      if (d.date <= sinceDate) return false;
      if (d.date > todayStr) return false;
      return true;
    })
    .reduce((s, d) => s + (parseInt(d.mortality_count as string) || 0), 0);
  const projected = Math.max(0, anchor - mortSince);
  return {anchor, anchorDate, projected, mortSince};
}

// ── Layer feed cost helper ─────────────────────────────────────────────────
// Computes total feed cost for a layer batch using the batch's frozen rates.
// Returns null if no rates are set.
export function computeLayerFeedCost(
  starterLbs: number | string | null | undefined,
  growerLbs: number | string | null | undefined,
  layerLbs: number | string | null | undefined,
  batch: LayerFeedRateBatch | null | undefined,
): number | null {
  if (!batch) return null;
  const sR = parseFloat(batch.per_lb_starter_cost as string);
  const gR = parseFloat(batch.per_lb_grower_cost as string);
  const lR = parseFloat(batch.per_lb_layer_cost as string);
  if ((!sR || sR === 0) && (!gR || gR === 0) && (!lR || lR === 0)) return null;
  const cost =
    (parseFloat(starterLbs as string) || 0) * (sR || 0) +
    (parseFloat(growerLbs as string) || 0) * (gR || 0) +
    (parseFloat(layerLbs as string) || 0) * (lR || 0);
  return cost;
}
